"use client"

import { useEffect, useState } from "react"
import { entityTypeDetailCache, useExplorer } from "@/hooks/use-explorer"
import type { AtsEntityTypeInfo } from "@/types/ats-explorer-api"

/**
 * Upgrades an entity-types listing entry to its full definition: the listing carries
 * only curated filters and defaults, while the per-entity endpoint adds everything the
 * vendor derives from metadata (Bullhorn: every filterable field, default columns for
 * uncurated entities). The listing entry is returned until the detail arrives — and
 * stays if the detail call fails — so callers never lose the curated set.
 */
export function useEntityTypeDetail(
  atsSystem: string | null,
  entity: AtsEntityTypeInfo | null
): { entity: AtsEntityTypeInfo | null; isLoading: boolean } {
  const { loadEntityType } = useExplorer()
  const [detail, setDetail] = useState<AtsEntityTypeInfo | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const entityName = entity?.name ?? null

  useEffect(() => {
    setDetail(null)
    if (!atsSystem || !entityName) return

    let cancelled = false
    const key = `${atsSystem}:${entityName}`
    let pending = entityTypeDetailCache.get(key)
    if (!pending) {
      pending = loadEntityType(atsSystem, entityName).catch((err) => {
        entityTypeDetailCache.delete(key)
        throw err
      })
      entityTypeDetailCache.set(key, pending)
    }

    setIsLoading(true)
    pending
      .then((loaded) => {
        if (!cancelled) setDetail(loaded)
      })
      .catch(() => {
        // The listing entry remains usable with its curated filters
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsSystem, entityName])

  return { entity: detail ?? entity, isLoading }
}
